import { useSetQuery } from "capitalrx-components-2.0";
import { formatPriceDisplay } from "capitalrx-utilities";
import cn from "classnames";
import { FC, useContext } from "react";
import { useNavigate } from "react-router";
import { Button } from "capitalrx-components-2.0/lib/general";
import AppleMap, { MapBounds, PharmacyMapCallout } from "../AppleMap";

import { PharmacyDetails } from "@/constants/routes";
import { ThemeSelectorContext } from "@/context/index";
import { Props } from "./FindBestPriceMap.props";
import styles from "./Map.module.scss";

const FindBestPriceMap: FC<Props> = ({
  pharmacyResults,
  isFetching,
  isLoading,
  defaultMapCoordinates,
  toggleMapView,
  mapView,
  updateCoordinates,
  allowRedoSearch,
  showInfoWindowButton = true,
}: Props) => {
  const [_, setQuery] = useSetQuery();
  const { client } = useContext(ThemeSelectorContext);

  const mapContainerStyles = cn(styles["MapContainer"], {
    [styles["ListView"]]: !mapView,
    [styles.MapWhitelabel]: !!client,
  });
  const navigate = useNavigate();

  const pharmacyListForMap = pharmacyResults?.map((pharmacy, idx) => {
    return {
      idx,
      distance: pharmacy.distance,
      pharmacy: pharmacy.pharmacy,
      price: pharmacy.price,
    };
  });

  const toggleViewButton = (
    <Button appearance="primary" icon="chevron-left" className={styles.ToggleMapViewButton} onClick={toggleMapView} testId="google-map-view-toggle" >
      List View
    </Button>
  );

  const redoSearchButton = (getCurrentBounds: () => MapBounds | null) => (
    !allowRedoSearch ? null : (
      <Button
        testId='redo-map-search-button'
        className={styles.redoSearchOnMap}
        onClick={() => {
          const bounds = getCurrentBounds();
          updateCoordinates({
            lat: bounds.center.latitude,
            lng: bounds.center.longitude,
          });
        }}>Redo Search on Map</Button>
    )
  );

  return (
    <div className={mapContainerStyles}>
      <AppleMap
        isLoading={isLoading}
        style={{ height: "100%" }}
        center={{
          latitude: defaultMapCoordinates.lat,
          longitude: defaultMapCoordinates.lng,
        }}
        locations={pharmacyListForMap?.map((item) => ({
          latitude: parseFloat(item.pharmacy.latitude),
          longitude: parseFloat(item.pharmacy.longitude),
          title: item.pharmacy.name,
          data: item,
        }))}
        renderCalloutContent={!showInfoWindowButton ? null : (item) => (
          <PharmacyMapCallout data={item.data.pharmacy} distance={item.data.distance} id={item.data.idx} />)
        }
        renderCalloutRightAccessory={!showInfoWindowButton ? null : (item) => {
          return (
            <div ref={(ref) => {
              const rawButton = ref.getElementsByTagName("button")[0];
              rawButton.onclick = () => {
                const pharmacy = item.data.pharmacy;
                const newUrlQueryParams = setQuery({
                  providerId: pharmacy.provider_id,
                  distance: item.data.distance,
                  price: item.data.price,
                });
                if (client) {
                  navigate({ pathname: `/client/${client}${PharmacyDetails}`, search: `?${newUrlQueryParams}` });
                } else {
                  navigate({ pathname: PharmacyDetails, search: `?${newUrlQueryParams}` });
                }
              };
            }}>
              <Button>{formatPriceDisplay(Number(item.data.price))}</Button>
            </div>
          );
        }}
        renderToolbar={(tools) => [
          toggleViewButton,
          redoSearchButton(tools.getCurrentBounds),
        ]}
      />
    </div>
  );
};

export default FindBestPriceMap;
